import type { HallMonth, RecapRef } from "./types";
import { recapLine } from "./recap-line.ts";
import { isHairstyle, isShirt } from "./shirts.ts";
import { isBeard, normalizeAccessories } from "./cosmetics.ts";
import { normalizeWornBadges } from "./achievements.ts";

/**
 * The Hall of Fame's memory (owner, 2026-10-02: "The peon of the month screen takes a weirdly long
 * time to load and only the frames load initially"). Two things make the Hall land at once: the
 * months it showed last time are kept, on the device when a store is given (so every launch after
 * the first paints them before its own request returns, not only a return visit); and the request starts on the
 * press that opens it (the Peon of the Month card's press-in), so the screen that mounts a slide
 * later finds it already on its way. Nothing here is authoritative: the screen still fetches and
 * replaces it, except that an answer which landed within FRESH_MS (a prefetch that beat the screen
 * to its mount) is the screen's answer, so one press never costs two requests. `load` is the
 * client's `api.stats.hall`; `now` is performance.now() unless a test passes its own clock.
 */
export const FRESH_MS = 2000;

/**
 * A warm (the Done board's mount) is skipped when an answer landed this recently: the board is
 * opened often and the Hall changes at most once per completed task.
 */
export const WARM_MS = 60_000;

/**
 * Where the months outlive the app (owner, 2026-10-02: "The peon hall of fame cards still take a
 * long time to load the content"; telemetry: 830 ms of request on every launch, 4 ms from memory).
 * `get` is synchronous so the screen's first render paints the rows; `set(null)` clears.
 */
export interface HallStore {
  get(): HallMonth[] | null;
  set(months: HallMonth[] | null): void;
}

/** The device key the months are kept under (localStorage on the web, lib/storage.ts on native). */
export const HALL_STORAGE_KEY = "op.hall";

/**
 * A HallStore over a synchronous key/value store. The months are kept with the household they
 * belong to (`household()`, the device's op.lastHousehold), and read back only while that is still
 * the household, so a household switch never paints the old household's months even where nothing
 * called forget (the web's switch reloads the page). Anything unreadable reads as nothing.
 */
export function hallStore(read: (key: string) => string | null, write: (key: string, value: string | null) => void, household: () => string | null): HallStore {
  return {
    get() {
      const id = household();
      return id ? parseStoredHall(read(HALL_STORAGE_KEY), id) : null;
    },
    set(months) {
      const id = household();
      write(HALL_STORAGE_KEY, months && id ? JSON.stringify({ household: id, months }) : null);
    },
  };
}

/** The stored months when they are well formed and `household`'s, else null. */
export function parseStoredHall(raw: string | null, household: string): HallMonth[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { household?: unknown; months?: unknown };
    if (!parsed || typeof parsed !== "object" || parsed.household !== household || !Array.isArray(parsed.months)) return null;
    const months: HallMonth[] = [];
    for (const m of parsed.months as Record<string, unknown>[]) {
      if (!m || typeof m !== "object" || typeof m.key !== "string" || typeof m.name !== "string" || typeof m.count !== "number") return null;
      const month: HallMonth = { key: m.key, name: m.name, count: m.count };
      if (m.shirt === null || isShirt(m.shirt)) month.shirt = m.shirt as HallMonth["shirt"];
      if (m.hair === null || isHairstyle(m.hair)) month.hair = m.hair as HallMonth["hair"];
      if (m.beard === null || isBeard(m.beard)) month.beard = m.beard as HallMonth["beard"];
      if (m.accessories === null) month.accessories = null;
      else if (Array.isArray(m.accessories)) month.accessories = normalizeAccessories(m.accessories);
      if (m.badges === null) month.badges = null;
      else if (Array.isArray(m.badges)) month.badges = normalizeWornBadges(m.badges);
      // The month's season recap (HallMonth.recap), kept so its card paints with its Watch recap;
      // a month saved before the field has none and paints as it did.
      if (m.recap === null) month.recap = null;
      else if (m.recap !== undefined) {
        const recap = hallRecapOf(m.recap);
        if (recap) month.recap = recap;
      }
      months.push(month);
    }
    return months;
  } catch {
    return null;
  }
}

/** A month's recap (HallMonth.recap) when it is well formed, else null. */
export function hallRecapOf(raw: unknown): RecapRef | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string" || !r.id || typeof r.title !== "string") return null;
  if (typeof r.month !== "string" || !/^\d{4}-\d{2}$/.test(r.month)) return null;
  if (typeof r.durationS !== "number" || !Number.isFinite(r.durationS)) return null;
  return { id: r.id, month: r.month, title: r.title, line: recapLine(r.line), durationS: r.durationS };
}

/**
 * The recap a Hall month's card offers as "Watch recap" (owner, 2026-11-02: "you should still be
 * able to watch previous month recaps"): with the `recapUI` flag on (the household's switch or this
 * device's), the month's own well-formed recap. Null with the flag off, with none, for a recap of
 * another month, and for a month saved before the field. Both clients' Halls read it.
 */
export function hallMonthRecap(enabled: boolean, month: { key: string; recap?: unknown }): RecapRef | null {
  if (!enabled) return null;
  const recap = hallRecapOf(month.recap);
  return recap && recap.month === month.key ? recap : null;
}

export interface HallMemory {
  /**
   * Starts the request now, or returns the one already in flight. The result is remembered.
   * `pressedAt` (performance.now() of the press that opens the Hall) is kept for the screen's timing.
   */
  prefetch(tz: string, pressedAt?: number): Promise<HallMonth[]>;
  /**
   * A prefetch with no press (the Done board's mount, where the crown card sits): joins a request
   * already out, and does nothing when an answer landed within WARM_MS. Never rejects.
   */
  warm(tz: string): Promise<void>;
  /** The press mark the last prefetch carried, once: the screen that mounts next takes it. */
  takePressedAt(): number | null;
  /** The screen's request: the one in flight when there is one, the answer that just landed, else a fresh one. */
  fetch(tz: string): Promise<HallMonth[]>;
  /** The months shown last time (this launch or, with a store, an earlier one), or null when there are none. */
  recall(): HallMonth[] | null;
  /** Keeps `months` as the last shown (the screen after a fetch; tests). */
  remember(months: HallMonth[]): void;
  /** After a household switch or sign-out nothing from the old household may paint. */
  forget(): void;
}

export function createHallMemory(
  load: (tz: string) => Promise<HallMonth[]>,
  now: () => number = () => performance.now(),
  store?: HallStore,
): HallMemory {
  // Read from the store the first time anything asks (synchronously), not at import: on the web a
  // household switch reloads the page, and by the time the Hall is asked for, auth has named the
  // household the store checks against.
  let stored: HallMonth[] | null | undefined = store ? undefined : null;
  const remembered = (): HallMonth[] | null => {
    if (stored === undefined) stored = store!.get();
    return stored;
  };
  const keep = (months: HallMonth[] | null) => {
    stored = months;
    store?.set(months);
  };
  let landedAt = -Infinity;
  let inFlight: Promise<HallMonth[]> | null = null;
  let generation = 0;
  let pressedAt: number | null = null;
  const memory: HallMemory = {
    prefetch(tz, at) {
      if (at !== undefined) pressedAt = at;
      if (inFlight) return inFlight;
      const started = generation;
      const request = load(tz).then(
        (months) => {
          // A forget while the request was out (a household switch) leaves nothing behind.
          if (started === generation) {
            keep(months);
            landedAt = now();
          }
          return months;
        },
      );
      request.finally(() => {
        if (inFlight === request) inFlight = null;
      }).catch(() => {});
      inFlight = request;
      return request;
    },
    warm(tz) {
      if (!inFlight && remembered() && now() - landedAt < WARM_MS) return Promise.resolve();
      return memory.prefetch(tz).then(
        () => {},
        () => {},
      );
    },
    fetch(tz) {
      if (inFlight) return inFlight;
      const months = remembered();
      if (months && now() - landedAt < FRESH_MS) return Promise.resolve(months);
      return memory.prefetch(tz);
    },
    takePressedAt() {
      const at = pressedAt;
      pressedAt = null;
      return at;
    },
    recall: () => remembered(),
    remember(months) {
      keep(months);
      landedAt = -Infinity;
    },
    forget() {
      keep(null);
      landedAt = -Infinity;
      inFlight = null;
      pressedAt = null;
      generation += 1;
    },
  };
  return memory;
}
